import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { PaymentStateService } from "../../services/payment-state.service";
import { RzpPaymentService } from "../../services/rzp-payment.service";
import { UtilService } from "../../services/util.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PaymentApiService } from "../../api/payment-api.service";
import Payment from "../../pojos/payment";

declare var window: any;

@Component({
  selector: "app-review-pay",
  templateUrl: "./review-pay.component.html",
  styleUrls: ["./review-pay.component.scss"],
  standalone: false,
})
export class ReviewPayComponent implements OnInit, OnDestroy {
  selectedPlan: any = null;
  billingInfo: any = null;
  isInternationalPayment = false;
  redirectUrl: string | null = null;
  selectedBranchName: string | null = null;
  isBranchNameMissing: boolean = false;
  qrCodeUrl: string | null = null;
  qrCodeId: string | null = null;
  qrPaymentId: string | null = null;
  isQrLoading = false;
  isQrImageLoading = false;
  isQrPolling = false;
  qrStatusMessage: string | null = null;
  // GST Logic Properties
  private readonly PLACE_OF_SUPPLY = "Karnataka";
  private qrPollingTimer: ReturnType<typeof setInterval> | null = null;
  private qrPollingStartedAt = 0;
  private readonly QR_POLL_INTERVAL_MS = 3000;
  private readonly QR_POLL_TIMEOUT_MS = 10 * 60 * 1000;

  constructor(
    private paymentState: PaymentStateService,
    private router: Router,
    private rzpService: RzpPaymentService,
    private util: UtilService,
    private snackbar: MatSnackBar,
    private paymentApi: PaymentApiService
  ) {}

  async ngOnInit() {
    // Check if we have required data
    if (
      !this.paymentState.isStep1Complete() ||
      !this.paymentState.isStep2Complete()
    ) {
      this.router.navigate(["/plan"]);
      return;
    }

    this.selectedPlan = this.paymentState.selectedPlan;
    this.billingInfo = this.paymentState.billingInfo;

    // Use the international payment flag from plan component (step 1)
    this.isInternationalPayment = this.paymentState.isInternationalPayment;

    // Get redirect URL from query params
    this.redirectUrl = new URLSearchParams(window.location.search).get(
      "redirectUrl"
    );

    // Fetch and display selected branch name
    await this.fetchSelectedBranchName();
  }

  ngOnDestroy(): void {
    this.stopQrPolling();
  }

  async fetchSelectedBranchName(): Promise<void> {
    try {
      if (this.paymentState.selectedBranchId) {
        const response = await this.paymentApi.getGymBranches();
        const branches = response.data || [];
        const selectedBranch = branches.find(
          (branch: any) => branch._id === this.paymentState.selectedBranchId
        );
        if (selectedBranch) {
          this.selectedBranchName = selectedBranch.subName;
          this.isBranchNameMissing = false;
        } else {
          // Branch ID exists but branch not found in the list
          this.selectedBranchName = null;
          this.isBranchNameMissing = true;
        }
      } else {
        // No branch ID selected
        this.selectedBranchName = null;
        this.isBranchNameMissing = true;
      }
    } catch (error) {
      console.error("Error fetching branch name:", error);
      this.selectedBranchName = null;
      this.isBranchNameMissing = true;
    }
  }

  goBackToBilling(): void {
    this.router.navigate(["/billing"]);
  }

  goBackToPlans(): void {
    this.router.navigate(["/plan"]);
  }

  // Helper methods for calculations
  calculateGstAmount(): number {
    if (!this.selectedPlan?.gstApplicable || this.isInternationalPayment) {
      return 0;
    }
    return this.selectedPlan.amount * this.selectedPlan.gstPercentage;
  }

  calculateTotalAmount(): number {
    const gstAmount = this.calculateGstAmount();
    return this.selectedPlan.amount + gstAmount;
  }

  formatCurrency(amount: number): string {
    const symbol = this.isInternationalPayment ? "$" : "₹";
    return `${symbol}${this.util.localizedCurrency(amount)}`;
  }

  hasDiscount(): boolean {
    return (
      !!this.selectedPlan?.originalAmount &&
      this.selectedPlan.originalAmount > this.selectedPlan.amount
    );
  }

  getSavingsAmount(): number {
    if (!this.hasDiscount()) {
      return 0;
    }
    return this.selectedPlan.originalAmount - this.selectedPlan.amount;
  }

  // GST Logic Methods
  getUserState(): string {
    const gstNumber = this.billingInfo?.gstNumber;
    const selectedState = this.billingInfo?.state;

    // If GST number is provided, extract state from GST number
    if (gstNumber && gstNumber.length >= 2) {
      const gstStateCode = gstNumber.substring(0, 2);
      return this.getStateNameFromGstCode(gstStateCode);
    }

    // Otherwise use selected state
    return selectedState || "";
  }

  getStateNameFromGstCode(gstCode: string): string {
    const stateCodeMap: { [key: string]: string } = {
      "01": "Jammu and Kashmir",
      "02": "Himachal Pradesh",
      "03": "Punjab",
      "04": "Chandigarh",
      "05": "Uttarakhand",
      "06": "Haryana",
      "07": "Delhi",
      "08": "Rajasthan",
      "09": "Uttar Pradesh",
      "10": "Bihar",
      "11": "Sikkim",
      "12": "Arunachal Pradesh",
      "13": "Nagaland",
      "14": "Manipur",
      "15": "Mizoram",
      "16": "Tripura",
      "17": "Meghalaya",
      "18": "Assam",
      "19": "West Bengal",
      "20": "Jharkhand",
      "21": "Odisha",
      "22": "Chhattisgarh",
      "23": "Madhya Pradesh",
      "24": "Gujarat",
      "25": "Daman and Diu",
      "26": "Dadra and Nagar Haveli",
      "27": "Maharashtra",
      "28": "Andhra Pradesh (Before Division)",
      "29": "Karnataka",
      "30": "Goa",
      "31": "Lakshadweep",
      "32": "Kerala",
      "33": "Tamil Nadu",
      "34": "Puducherry",
      "35": "Andaman and Nicobar Islands",
      "36": "Telangana",
      "37": "Andhra Pradesh (New)",
      "38": "Ladakh",
    };

    // Extract first two digits (GSTIN starts with 2-digit state code)
    const code = gstCode?.substring(0, 2);
    return stateCodeMap[code] || "";
  }

  isSameState(): boolean {
    const userState = this.getUserState();
    return userState === this.PLACE_OF_SUPPLY;
  }

  getGstType(): "SGST+CGST" | "IGST" {
    return this.isSameState() ? "SGST+CGST" : "IGST";
  }

  getGstBreakdown(): {
    type: string;
    percentage: number;
    amount: number;
  }[] {
    const isSame = this.isSameState();
    const planAmount = this.selectedPlan?.amount || 0;
    const gstAmount = planAmount * 0.18; // 18% total GST

    if (isSame) {
      const sgstAmount = gstAmount / 2; // 9% each
      const cgstAmount = gstAmount / 2; // 9% each

      return [
        {
          type: "SGST",
          percentage: 9,
          amount: sgstAmount,
        },
        {
          type: "CGST",
          percentage: 9,
          amount: cgstAmount,
        },
      ];
    } else {
      return [
        {
          type: "IGST",
          percentage: 18,
          amount: gstAmount,
        },
      ];
    }
  }

  getGstPercentage(): number {
    return this.selectedPlan?.gstPercentage * 100 || 0;
  }

  isGstClaimable(): boolean {
    return this.billingInfo?.hasGST && this.billingInfo?.gstNumber;
  }

  async generateQRCode(): Promise<void> {
    if (this.isInternationalPayment) {
      this.snackbar.open("UPI QR is available for India payments only", "Close", {
        duration: 3000,
      });
      return;
    }

    if (!this.selectedPlan) {
      this.snackbar.open("Please select a plan first", "Close", {
        duration: 3000,
      });
      return;
    }

    if (
      !this.paymentState.selectedBranchId ||
      this.isBranchNameMissing ||
      !this.selectedBranchName
    ) {
      this.snackbar.open(
        "Please go back and select a gym branch first",
        "Close",
        {
          duration: 3000,
        }
      );
      return;
    }

    this.isQrLoading = true;
    this.qrStatusMessage = null;
    this.stopQrPolling();
    try {
      let payment: Payment =
        await this.rzpService.createPaymentRequestForGymManagementAtRzp(
          this.selectedPlan.planId,
          this.paymentState.selectedBranchId,
          this.billingInfo
        );

      const response: any = await this.paymentApi.createPaymentQRCode(
        payment.notes.paymentId
      );
      this.qrCodeUrl = response.data.imageUrl;
      this.qrCodeId = response.data.qrCodeId;
      this.qrPaymentId = payment.notes.paymentId;
      this.isQrImageLoading = true;
      this.startQrPolling();
    } catch (error) {
      this.snackbar.open(
        "Failed to generate QR code. Please try again.",
        "Close",
        {
          duration: 3000,
        }
      );
    } finally {
      this.isQrLoading = false;
    }
  }

  private startQrPolling(): void {
    if (!this.qrPaymentId) {
      return;
    }

    this.qrPollingStartedAt = Date.now();
    this.isQrPolling = true;
    this.qrStatusMessage = "Waiting for payment confirmation...";
    this.checkQrPaymentStatus();
    this.qrPollingTimer = setInterval(() => {
      this.checkQrPaymentStatus();
    }, this.QR_POLL_INTERVAL_MS);
  }

  private stopQrPolling(): void {
    if (this.qrPollingTimer) {
      clearInterval(this.qrPollingTimer);
      this.qrPollingTimer = null;
    }
    this.isQrPolling = false;
  }

  private async checkQrPaymentStatus(): Promise<void> {
    if (!this.qrPaymentId) {
      this.stopQrPolling();
      return;
    }

    if (Date.now() - this.qrPollingStartedAt > this.QR_POLL_TIMEOUT_MS) {
      this.stopQrPolling();
      this.qrStatusMessage =
        "Payment confirmation timed out. Please refresh and check again.";
      this.snackbar.open("Payment confirmation timed out.", "Close", {
        duration: 4000,
      });
      return;
    }

    try {
      const response: any = await this.paymentApi.getPaymentStatus(
        this.qrPaymentId
      );
      const paymentStatus = response?.data?.paymentStatus;

      if (response?.data?.isPaid || paymentStatus === "paid") {
        this.stopQrPolling();
        this.qrStatusMessage = "Payment received. Redirecting...";
        this.redirectAfterSuccessfulPayment();
        return;
      }

      if (response?.data?.isFailed || paymentStatus === "failed") {
        this.stopQrPolling();
        this.qrStatusMessage = "Payment failed. Please try again.";
        this.snackbar.open("Payment failed. Please try again.", "Close", {
          duration: 4000,
        });
      }
    } catch (error) {
      this.qrStatusMessage =
        "Unable to check payment status. Retrying automatically...";
    }
  }

  private redirectAfterSuccessfulPayment(): void {
    if (this.util.isNotNull(this.redirectUrl)) {
      window.open(this.redirectUrl, "_self");
    } else {
      window.open(location.origin + "/payment?s=s", "_self");
    }
  }

  onQrImageLoad(): void {
    this.isQrImageLoading = false;
  }

  onQrImageError(): void {
    this.isQrImageLoading = false;
    this.snackbar.open("Unable to load QR image. Please try again.", "Close", {
      duration: 3000,
    });
  }

  viewQR(): void {
    window.open(this.qrCodeUrl!, "_blank");
  }

  downloadQR(): void {
    const link = document.createElement("a");
    link.href = this.qrCodeUrl!;
    link.download = "gymbook-payment-qr.png";
    link.target = "_blank";
    link.click();
  }

  async payNow(): Promise<void> {
    if (!this.selectedPlan) {
      this.snackbar.open("Please select a plan first", "Close", {
        duration: 3000,
      });
      return;
    }

    if (
      !this.paymentState.selectedBranchId ||
      this.isBranchNameMissing ||
      !this.selectedBranchName
    ) {
      this.snackbar.open(
        "Please go back and select a gym branch first",
        "Close",
        {
          duration: 3000,
        }
      );
      return;
    }

    try {
      let payment: Payment =
        await this.rzpService.createPaymentRequestForGymManagementAtRzp(
          this.selectedPlan.planId,
          this.paymentState.selectedBranchId,
          this.billingInfo
        );

      let orderId = await this.rzpService.createRzpOrder(
        payment.finalAmount,
        payment.notes.paymentId,
        payment.currency
      );

      await this.rzpService.acceptPaymentFromRzp(payment, orderId);

      this.redirectAfterSuccessfulPayment();
    } catch (error) {
      window.open(location.origin + "/payment?s=f", "_self");
    }
  }
}
